// src/components/CategoryList.js
import React, { useRef, useEffect, useState } from 'react';

const CategoryList = ({ 
  categories, 
  selectedCategory, 
  onCategoryClick, 
  primaryColor,
  language = 'en' // 'en' or 'km'
}) => {
  const scrollContainerRef = useRef(null);
  const [navbarHeight, setNavbarHeight] = useState(72);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Translations
  const translations = {
    en: {
      allProducts: 'All Products'
    },
    km: {
      allProducts: 'ផលិតផលទាំងអស់'
    }
  };

  const t = translations[language] || translations.en;

  // Get dynamic navbar height
  useEffect(() => {
    const updateNavbarHeight = () => {
      const navbar = document.querySelector('nav');
      if (navbar) {
        const height = navbar.offsetHeight;
        setNavbarHeight(height);
      }
    };
    
    updateNavbarHeight();
    window.addEventListener('resize', updateNavbarHeight);
    
    return () => window.removeEventListener('resize', updateNavbarHeight);
  }, []);

  // Check scroll position for arrows
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [categories]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -250 : 250;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!categories || categories.length === 0) {
    return null;
  }

  // Define font family based on language
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  return (
    <>
      {/* Google Fonts - Both English and Khmer */}
      <link 
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Kantumruy+Pro:wght@300;400;500;600;700&family=Noto+Sans+Khmer:wght@100;200;300;400;500;600;700;800;900&family=Noto+Serif+Khmer:wght@100;200;300;400;500;600;700;800;900&display=swap" 
        rel="stylesheet" 
      />
      
      <style>{`
        .category-font-${language} {
          font-family: ${getFontFamily()} !important;
        }
        .category-btn-${language} {
          font-family: ${getFontFamily()} !important;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
        }
        .category-btn-${language}::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background-color: ${primaryColor};
          transition: all 0.3s ease;
          transform: translateX(-50%);
        }
        .category-btn-${language}:hover::after {
          width: 80%;
        }
        .category-btn-${language}.active::after {
          width: 80%;
        }
        .scroll-arrow-${language} {
          backdrop-filter: blur(8px);
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.06);
        }
        .scroll-arrow-${language}:hover {
          background: rgba(255, 255, 255, 1);
          border-color: rgba(0, 0, 0, 0.12);
          transform: translateY(-50%) scale(1.05);
        }
        /* Khmer specific styles */
        ${language === 'km' ? `
          .category-btn-${language} {
            font-weight: 400 !important;
            line-height: 1.8 !important;
            letter-spacing: 0.5px !important;
          }
          .category-btn-${language}.active {
            font-weight: 500 !important;
          }
        ` : ''}
      `}</style>

      <div 
        className={`sticky z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100/80 shadow-sm category-font-${language}`}
        style={{ 
          top: `${navbarHeight}px`,
          fontFamily: getFontFamily()
        }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-2 md:py-3">
          {/* Horizontal Scroll Container */}
          <div className="relative">
            {/* Left Scroll Button */}
            {showLeftArrow && (
              <button
                onClick={() => scroll('left')}
                className={`absolute -left-2 md:-left-3 top-1/2 -translate-y-1/2 z-10 scroll-arrow-${language} shadow-md p-2 md:p-2.5 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-900 focus:outline-none transition-all duration-200`}
                aria-label="Scroll left"
                style={{ fontFamily: getFontFamily() }}
              >
                <i className="fas fa-chevron-left text-xs md:text-sm"></i>
              </button>
            )}
            
            {/* Scrollable Categories */}
            <div
              ref={scrollContainerRef}
              className="flex overflow-x-auto scrollbar-hide gap-1.5 md:gap-2.5 py-1"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              <style>{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              
              {/* All Products Button */}
              <button
                onClick={() => onCategoryClick(null)}
                className={`category-btn-${language} px-4 md:px-6 py-1.5 md:py-2.5 font-medium rounded-full flex-shrink-0 text-xs md:text-sm whitespace-nowrap ${
                  selectedCategory === null
                    ? 'active text-white shadow-md'
                    : 'bg-gray-100/80 text-gray-700 hover:bg-gray-200/80 hover:shadow-sm'
                }`}
                style={selectedCategory === null ? { 
                  backgroundColor: primaryColor,
                  fontFamily: getFontFamily()
                } : { fontFamily: getFontFamily() }}
              >
                {t.allProducts}
              </button>
              
              {/* Category Buttons */}
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => onCategoryClick(category.id)}
                  className={`category-btn-${language} px-4 md:px-6 py-1.5 md:py-2.5 font-medium rounded-full flex-shrink-0 text-xs md:text-sm whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'active text-white shadow-md'
                      : 'bg-gray-100/80 text-gray-700 hover:bg-gray-200/80 hover:shadow-sm'
                  }`}
                  style={selectedCategory === category.id ? { 
                    backgroundColor: primaryColor,
                    fontFamily: getFontFamily()
                  } : { fontFamily: getFontFamily() }}
                >
                  {category.title}
                </button>
              ))}
            </div>
            
            {/* Right Scroll Button */}
            {showRightArrow && (
              <button
                onClick={() => scroll('right')}
                className={`absolute -right-2 md:-right-3 top-1/2 -translate-y-1/2 z-10 scroll-arrow-${language} shadow-md p-2 md:p-2.5 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-900 focus:outline-none transition-all duration-200`}
                aria-label="Scroll right"
                style={{ fontFamily: getFontFamily() }}
              >
                <i className="fas fa-chevron-right text-xs md:text-sm"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryList;